import { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import makeStyles from '@mui/styles/makeStyles';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { unwrapResult } from '@reduxjs/toolkit';
import { useSnackbar } from 'notistack';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import { Exception, PaymentMethodType } from '../../models';
import { fromAccounts, fromPaymentMethod, useAppDispatch } from '../../store';
import { Page } from '../../components/page';
import { useQuery } from '../../routes';
import { LoadingProgress } from '../../layout';
import { PaymentInformationSection } from '../../components';
import { Panel } from '../../components/common/Panel';

const useStyles = makeStyles(theme => ({
  root: {
    paddingRight: theme.spacing(0),
    width: '100%',
    backgroundColor: theme.palette.background.paper,
    [theme.breakpoints.down('dmd')]: {
      paddingRight: 0,
    },
  },
  dialogPaper: {
    minWidth: 600,
    paddingBottom: theme.spacing(2),
    paddingRight: theme.spacing(2),
    paddingLeft: theme.spacing(2),
  },
}));

const PaymentMethod = () => {
  const classes = useStyles();
  const { get } = useQuery();
  const dispatch = useAppDispatch();
  const accountId = Number(get('accountId'));
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const account = useSelector(fromAccounts.selectAccountById(Number(accountId)));
  const currentDefaultPaymentMethod = useSelector(fromPaymentMethod.selectDefaultPaymentMethod);
  const [selectedDefaultPaymentMethod, setSelectedDefaultPaymentMethod] = useState(
    currentDefaultPaymentMethod,
  );
  const [defaultPaymentMethodDialogOpen, setDefaultPaymentMethodDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSelectPaymentMethod = (paymentMethod: PaymentMethodType) => {
    if (paymentMethod !== selectedDefaultPaymentMethod) {
      setSelectedDefaultPaymentMethod(paymentMethod);
      setDefaultPaymentMethodDialogOpen(true);
    }
  };

  const fetchInfo = async () => {
    if (accountId) {
      await Promise.all([
        dispatch(fromAccounts.doCheckPublicDomain(accountId)),
        dispatch(fromPaymentMethod.doGetDefaultPaymentMethod()),
      ]);
    }
  };

  useEffect(() => {
    setSelectedDefaultPaymentMethod(currentDefaultPaymentMethod);
  }, [currentDefaultPaymentMethod]);

  const updateDefaultPaymentMethod = async (paymentMethod: PaymentMethodType) => {
    setLoading(true);
    await dispatch(fromPaymentMethod.doUpdateDefaultPaymentMethod({
      defaultPaymentMethod: paymentMethod,
    }))
      .then(unwrapResult)
      .then(() => {
        setDefaultPaymentMethodDialogOpen(false);
        setSelectedDefaultPaymentMethod(paymentMethod);
        dispatch(fromPaymentMethod.doChangeSelectedPaymentMethod(paymentMethod));
        enqueueSnackbar(<FormattedMessage
          id="billinginfo.default_payment_method.change.success"
        />, { variant: 'success' });
      })
      .catch((ex: Exception) => enqueueSnackbar(<span>{ex.message}</span>, { variant: 'error' }))
      .finally(() => setLoading(false));
  };

  const handleCancelDefaultPaymentMethod = () => {
    setSelectedDefaultPaymentMethod(currentDefaultPaymentMethod);
    setDefaultPaymentMethodDialogOpen(false);
  };

  const handleUpdateDefaultPaymentMethod = async () => selectedDefaultPaymentMethod
    && updateDefaultPaymentMethod(selectedDefaultPaymentMethod);

  const renderSelectDefaultPaymentMethodDialog = () => (
    <Dialog
      open={defaultPaymentMethodDialogOpen}
      onClose={() => setDefaultPaymentMethodDialogOpen(false)}
      classes={{
        paper: classes.dialogPaper,
      }}
    >
      <DialogTitle>
        <FormattedMessage
          id="billinginfo.default_payment_method.dialog.title"
          values={{
            paymentMethod: intl.formatMessage({ id: `billinginfo.payment_method.${selectedDefaultPaymentMethod}` }),
          }}
        />
      </DialogTitle>
      <DialogContent>
        <Typography>
          <FormattedMessage
            id="billinginfo.default_payment_method.dialog.content"
            values={{ accountName: account?.name }}
          />
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleCancelDefaultPaymentMethod}
          variant="text"
          disabled={loading}
          sx={{
            color: '#1847ca',
            fontSize: 14,
            fontWeight: '500',
          }}
        >
          <FormattedMessage id="billinginfo.default_payment_method.dialog.cancel" />
        </Button>
        <Button
          onClick={handleUpdateDefaultPaymentMethod}
          variant="contained"
          color="primary"
          disabled={loading}
          sx={{
            backgroundColor: '#1847ca',
            fontSize: 14,
            fontWeight: '500',
            color: '#fff',
            '&:hover': {
              boxShadow: '0px 6px 8px rgba(209, 209, 209, 0.6)',
              backgroundColor: '#2A3DC7',
            },
          }}
        >
          <FormattedMessage id="billinginfo.default_payment_method.dialog.confirm" />
        </Button>
      </DialogActions>
    </Dialog>
  );

  return loading ? <LoadingProgress /> : (
    <Page
      id="payment-checkout"
      headerId="page.payment.payment.title"
      fetchData={fetchInfo}
    >
      <Panel
        id="payment-method-panel"
        sx={theme => ({
          overflow: 'scroll',
          margin: theme.spacing(4, 0),
        })}
        border
      >
        <Box
          className={classes.root}
          p={4}
        >
          <PaymentInformationSection
            accountId={accountId}
            handleSelectPaymentMethod={handleSelectPaymentMethod}
            enableSave
            enableDeleteCard
            availableRenewal
            showCardInformation
          />
          {renderSelectDefaultPaymentMethodDialog()}
        </Box>
      </Panel>
    </Page>
  );
};

export default PaymentMethod;
